// The alerts strip above the authorizations table: exhausted, expiring, missing.
//
// It is the reason this page exists. The table answers "what authorizations do we
// hold"; the strip answers "what do I have to do today", which is the question a
// biller actually arrives with.
//
// Three deliberate choices:
//   * A kind with a count of zero is not rendered. Three grey zeroes read as a
//     dashboard; nothing at all reads as nothing to do, which is the truth.
//   * `truncated` is surfaced verbatim. The backend measures it on rows scanned,
//     so it means "this is a floor, not a total" — hiding that would turn "we
//     might have missed some" into "we definitely did not".
//   * `missing` carries no authorization, so its rows name the claim or the
//     appointment instead. Clicking one does not navigate: the auth does not
//     exist yet, so there is nothing to open.

import React, { useState } from "react";

const KIND_ORDER = ["exhausted", "expiring", "missing"];

const KIND_LABELS = {
  exhausted: {
    one: "authorization is used up",
    many: "authorizations are used up",
  },
  expiring: {
    one: "authorization expires soon",
    many: "authorizations expire soon",
  },
  missing: {
    one: "visit has no authorization",
    many: "visits have no authorization",
  },
};

function label(kind, count) {
  const pair = KIND_LABELS[kind];
  if (!pair) return kind;
  return `${count} ${count === 1 ? pair.one : pair.many}`;
}

function AlertRow({ alert, onCreate }) {
  const subject = alert.subject;
  if (alert.auth) {
    return (
      <li className="auth-alert-row">
        <span className="auth-alert-who">
          {alert.auth.patient_name || "Unmatched patient"}
        </span>
        <span className="auth-alert-sep">·</span>
        <span>{alert.auth.payer_name || "No payer"}</span>
        <span className="auth-alert-sep">·</span>
        <span className="auth-alert-number">{alert.auth.auth_number}</span>
        <span className="auth-alert-why">{alert.reason}</span>
      </li>
    );
  }
  // A `missing` alert is the one row on this page that is a to-do rather than a
  // record: the authorization does not exist, so the useful action is to create
  // it. Clicking hands the modal what the alert already knows — patient, payer,
  // and the date that needs covering — instead of making the biller copy three
  // fields out of a sentence.
  return (
    <li className={`auth-alert-row${onCreate ? " auth-alert-row-actionable" : ""}`}>
      <span className="auth-alert-who">
        {(subject && subject.patient_name) || "Unmatched patient"}
      </span>
      <span className="auth-alert-sep">·</span>
      <span>{(subject && subject.payer_name) || "No payer"}</span>
      <span className="auth-alert-sep">·</span>
      <span>{(subject && subject.date_of_service) || "No date"}</span>
      <span className="auth-alert-kind">
        {subject && subject.appointment_id
          ? "upcoming visit"
          : "unbilled claim"}
      </span>
      <span className="auth-alert-why">{alert.reason}</span>
      {onCreate && (
        <button
          type="button"
          className="auth-btn auth-btn-link auth-alert-action"
          onClick={() => onCreate(subject)}
        >
          Add authorization
        </button>
      )}
    </li>
  );
}

export default function AlertsStrip({
  alerts,
  counts,
  truncated,
  loading,
  error,
  onCreate,
}) {
  const [open, setOpen] = useState(null);

  if (loading) {
    return (
      <div className="auth-alerts auth-alerts-quiet">
        Checking authorizations…
      </div>
    );
  }

  // "We could not check" and "we checked and found nothing" must never look the
  // same. The all-clear below is a positive assertion of safety, and the point
  // of the missing-auth alert is to stop someone billing an unauthorized visit —
  // so a failed fetch has to say so, even when the table beside it is fine.
  // `/alerts` sweeps claims and appointments and is much the more expensive
  // call, so it failing on its own is the realistic case, not a corner.
  // `!counts` covers every way we can fail to know, not just the ones the page
  // remembered to flag: never fetched, fetched and unparseable, fetched into an
  // unexpected shape. The all-clear below asserts safety, so it requires a real
  // answer rather than merely the absence of a recorded error.
  // Belt to the loader's braces, and the same rule: a `counts` object whose
  // kinds are not numbers is not an answer. `{}` is truthy.
  const known =
    counts && KIND_ORDER.every((kind) => Number.isFinite(counts[kind]));
  if (error || !known) {
    return (
      <div className="auth-alerts auth-alerts-failed">
        <strong>Could not check authorizations.</strong> This strip normally lists
        authorizations that are used up or expiring, and visits with no
        authorization — treat it as unknown rather than clear, and reload to try
        again.
      </div>
    );
  }

  const live = KIND_ORDER.filter((kind) => counts[kind] > 0);
  if (live.length === 0) {
    return (
      <div className="auth-alerts auth-alerts-quiet">
        Nothing needs attention — no authorization is used up, expiring within
        14 days, or missing for a payer that requires one.
      </div>
    );
  }

  const shown = open ? (alerts || []).filter((a) => a.kind === open) : [];

  return (
    <div className="auth-alerts">
      <div className="auth-alerts-pills">
        {live.map((kind) => (
          <button
            key={kind}
            type="button"
            className={`auth-pill auth-pill-${kind}${open === kind ? " auth-pill-open" : ""}`}
            onClick={() => setOpen(open === kind ? null : kind)}
            aria-expanded={open === kind}
          >
            {label(kind, counts[kind])}
          </button>
        ))}
        {truncated && (
          <span
            className="auth-alerts-truncated"
            title="The scan stopped at its row limit, so these counts are a floor rather than a total."
          >
            showing the first batch only
          </span>
        )}
      </div>
      {open && shown.length > 0 && (
        <ul className="auth-alert-list">
          {shown.map((alert, i) => (
            <AlertRow
              key={`${alert.kind}-${i}`}
              alert={alert}
              onCreate={alert.kind === "missing" ? onCreate : null}
            />
          ))}
        </ul>
      )}
    </div>
  );
}
